import React, { useContext, useEffect, useReducer, useRef, useCallback } from 'react';
import {
	GameDevelopersContext,
	gameDevelopersInitialState,
	IFilterValues,
	TGameDevelopersCountOfItems,
	TGameDevelopersSortBy,
	TTypeOfModalWindow,
} from './gameDevelopersContext';
import { AppContext, IAppContext } from 'appState/appContext';
import { gameDevelopersReducer } from './gameDevelopersReducer';
import { GameDevelopersActionTypes } from './gameDevelopersActions';
import { useFetchData } from 'hooks/useFetchData';
import { getPaginatedGameDevelopers, deleteGameDeveloper } from 'backend/games';
import { AxiosResponse } from 'axios';
import { IGameDevelopers, IGetPaginatedGameDevelopers } from 'types';

const GameDevelopersState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(gameDevelopersReducer, gameDevelopersInitialState);
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);
	const isMounted = useRef<boolean>(false);
	const { refetch, response, loading } = useFetchData<AxiosResponse<IGameDevelopers>, IGetPaginatedGameDevelopers>(
		isMounted.current ? getPaginatedGameDevelopers : null,
		{
			pageNumber: state.currentPage,
			pageSize: Number(state.countOfItems),
			searchByName: state.searchString ? state.searchString : null,
			sortByName: state.sortBy === 'NameA-Z' || state.sortBy === 'NameZ-A' ? state.sortBy : null,
		},
	);

	useEffect(() => {
		loading ? showLoader() : hideLoader();
	}, [loading]);

	useEffect(() => {
		if (response) {
			dispatch({
				type: GameDevelopersActionTypes.SET_GAME_DEVELOPERS,
				payload: {
					gameDevelopers: response.data,
					typeOfModalWindow: state.typeOfModalWindow,
				},
			});
		}
	}, [response]);

	const deleteSelectedItem = useCallback(async () => {
		try {
			showLoader();
			await deleteGameDeveloper(state.IDForDeletion);
			const filteredGameDevelopers = state.gameDevelopers.gameDevelopers.filter(
				(el) => el.id !== state.IDForDeletion,
			);
			dispatch({
				type: GameDevelopersActionTypes.SET_GAME_DEVELOPERS,
				payload: {
					gameDevelopers: {
						gameDevelopersCount: state.gameDevelopers.gameDevelopersCount - 1,
						gameDevelopers: filteredGameDevelopers,
					},
					typeOfModalWindow: 'success',
				},
			});
		} catch (err) {
			dispatch({
				type: GameDevelopersActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
				payload: {
					typeOfModalWindow: 'error',
				},
			});
		} finally {
			hideLoader();
		}
	}, [showLoader, hideLoader, state.IDForDeletion, state.gameDevelopers]);

	const changeSearchString = (value: string) => {
		dispatch({
			type: GameDevelopersActionTypes.CHANGE_SEARCH_STRING,
			payload: {
				searchString: value,
			},
		});
	};

	const resetSelectedControls = () => dispatch({ type: GameDevelopersActionTypes.RESET_SELECTED_CONTROLS });

	const setSortBy = (sortBy: TGameDevelopersSortBy) => {
		dispatch({
			type: GameDevelopersActionTypes.SET_SORT_BY,
			payload: {
				sortBy: sortBy,
			},
		});
	};

	const setCountOfItems = (count: TGameDevelopersCountOfItems) => {
		dispatch({
			type: GameDevelopersActionTypes.SET_COUNT_OF_ITEMS,
			payload: {
				countOfItems: count,
			},
		});
	};

	const changeCurrentPage = (page: number) => {
		dispatch({
			type: GameDevelopersActionTypes.CHANGE_CURRENT_PAGE,
			payload: {
				currentPage: page,
			},
		});
	};

	const setIDForDeletion = (value: string) => {
		dispatch({
			type: GameDevelopersActionTypes.SET_ID_FOR_DELETION,
			payload: {
				IDForDeletion: value,
				typeOfModalWindow: 'toConfirmDeleteItem',
			},
		});
	};

	const setIDForEdit = (value: string) => {
		dispatch({
			type: GameDevelopersActionTypes.SET_ID_FOR_EDIT,
			payload: {
				IDForEdit: value,
				typeOfModalWindow: 'editDeveloper',
			},
		});
	};

	const changeTypeOfModalWindow = (value: TTypeOfModalWindow) => {
		dispatch({
			type: GameDevelopersActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	};

	const setFiltersFromQueryString = (value: IFilterValues) => {
		dispatch({
			type: GameDevelopersActionTypes.SET_FILTERS_FROM_QUERY_STRING,
			payload: {
				filters: value,
			},
		});
	};

	const refetchDevelopers = (value: boolean) => {
		dispatch({
			type: GameDevelopersActionTypes.REFETCH_DEVELOPERS,
			payload: {
				needRefetchDevelopers: value,
			},
		});
	};

	useEffect(() => {
		isMounted.current = true;
		refetch();
	}, [state.currentPage, state.countOfItems, state.searchString, state.sortBy]);

	useEffect(() => {
		if (state.needRefetchDevelopers) {
			refetch();
			refetchDevelopers(false);
		}
	}, [state.needRefetchDevelopers]);

	return (
		<GameDevelopersContext.Provider
			value={{
				deleteSelectedItem,
				changeSearchString,
				resetSelectedControls,
				setSortBy,
				setCountOfItems,
				changeCurrentPage,
				setIDForDeletion,
				setIDForEdit,
				changeTypeOfModalWindow,
				setFiltersFromQueryString,
				refetchDevelopers,
				gameDevelopers: state.gameDevelopers,
				searchString: state.searchString,
				sortBy: state.sortBy,
				countOfItems: state.countOfItems,
				currentPage: state.currentPage,
				IDForDeletion: state.IDForDeletion,
				IDForEdit: state.IDForEdit,
				typeOfModalWindow: state.typeOfModalWindow,
				needRefetchDevelopers: state.needRefetchDevelopers,
			}}>
			{children}
		</GameDevelopersContext.Provider>
	);
};

export default GameDevelopersState;
